// PartyBadge.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  partyColor,
  partyKeyFromLabel,
  partyName,
  partyPath,
  partyShortName,
} from '../../../utils/parties';
import './Parties.css';

/**
 * PartyBadge
 *
 * A candidate's party as printed on the ballot, with a color dot, linking to
 * the party page where the label names a party. Meant to replace the bare
 * party text anywhere a candidate is shown: detail pages, list rows, the
 * ballot.
 *
 * The label shown is always the one the state printed ("Democratic-Farmer-
 * Labor", not "Democratic Party"), because that is what is on the voter's
 * ballot. Only the link resolves it to a party.
 *
 * partyKey wins when the row has one -- /elections/ballot supplies it. Rows
 * from the local candidate endpoints carry only the label, and fall back to
 * partyKeyFromLabel, which resolves only labels that name the party outright.
 * An unresolved label renders as plain text: "Independent" is a real answer,
 * not a missing link.
 *
 * The click stops propagating because it usually sits inside a row that is
 * itself clickable, and tapping the party should not also open the candidate.
 */

function PartyBadge({ label, partyKey, stateAbbr, className = '' }) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const key = partyKey || partyKeyFromLabel(label);
  const text = label || (key ? partyShortName(key) : '');
  if (!text) return null;

  const dot = (
    <span className="party-badge-dot" style={{ backgroundColor: partyColor(key) }} aria-hidden />
  );

  if (!key) {
    return (
      <span className={`party-badge party-badge--plain ${className}`}>
        {dot}
        {text}
      </span>
    );
  }

  const go = (event) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(partyPath(key, stateAbbr));
  };

  return (
    <button
      type="button"
      className={`party-badge ${className}`}
      onClick={go}
      aria-label={t('common:viewPartyNamed', 'View the {{party}}', {
        party: partyName(key),
      })}
    >
      {dot}
      {text}
    </button>
  );
}

export default PartyBadge;